import { homeSecurityPortalApi } from 'data/axios';
import {
  TPaymentMethodsList,
  TPurchasePayload,
  TSubscriptionType,
  TTransactionList
} from 'typings';
import { ApiResponse } from './HttpRequest';
import { PIGGYBANK_ENDPOINTS } from './endpoints';

export async function GetAllPaymentMethods(): Promise<
  ApiResponse<TPaymentMethodsList>
> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.PAYMENT_METHODS
  );
  return response;
}

export async function DeletePaymentMethod(
  paymentMethodId: string
): Promise<ApiResponse<{ message: string }>> {
  const response = await homeSecurityPortalApi.delete(
    PIGGYBANK_ENDPOINTS.DELETE_PM(paymentMethodId)
  );
  return response;
}

export async function UpdateDefaultPaymentMethod(
  paymentMethodId: string
): Promise<ApiResponse<{ message: string }>> {
  const response = await homeSecurityPortalApi.put(
    PIGGYBANK_ENDPOINTS.UPDATE_DEFAULT_PM(paymentMethodId)
  );
  return response;
}

export async function PaymentSetupIntent() {
  try {
    const response = await homeSecurityPortalApi.post(
      PIGGYBANK_ENDPOINTS.SETUP_INTENT
    );
    return response;
  } catch (error) {
    console.error('Error fetching invoice link:', error);
    throw error;
  }
}

export async function GetAllTransaction(): Promise<
  ApiResponse<TTransactionList>
> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.GET_TRANSACTION
  );
  return response;
}

export async function GetInvoiceDownloadLink(transactionId: string) {
  try {
    const response = await homeSecurityPortalApi.get(
      PIGGYBANK_ENDPOINTS.DOWNLOAD_INVOICES(transactionId)
    );
    return response;
  } catch (error) {
    console.error('Error fetching invoice link:', error);
    throw error;
  }
}

export async function CreatePurchaseId(payload: TPurchasePayload) {
  try {
    const response = await homeSecurityPortalApi.post(
      PIGGYBANK_ENDPOINTS.CREATE_PURCHASE_ID,
      payload
    );
    return response;
  } catch (error) {
    console.error('Error creating purchase ID:', error);
    throw error;
  }
}

export async function GetProRataCharge(purchaseId: string) {
  try {
    const response = await homeSecurityPortalApi.get(
      PIGGYBANK_ENDPOINTS.PRORATE_CHARGE(purchaseId)
    );
    return response;
  } catch (error) {
    console.error('Error fetching pro-rata charge:', error);
    throw error;
  }
}

type TProrateChargesPayloadV2 = {
  sku_id: string;
};

export async function fetchProRataCharge(
  purchaseId: string,
  payload?: TProrateChargesPayloadV2
) {
  try {
    const response = await homeSecurityPortalApi.post(
      PIGGYBANK_ENDPOINTS.PRORATE_CHARGE(purchaseId),
      payload
    );
    return response;
  } catch (error) {
    console.error('Error fetching pro-rata charge:', error);
    throw error;
  }
}

export async function fetchProrateCharges(subscriptionId: string) {
  try {
    const response = await homeSecurityPortalApi.post(
      PIGGYBANK_ENDPOINTS.PRORATE_CHARGE(subscriptionId)
    );
    return response;
  } catch (error) {
    console.error('Error in fetching prorate charges:', error);
    throw error;
  }
}

// Spec shows a bare `[purchase]` array; existing wrapped `{ items }` responses
// are also handled by the reducer. Typed as a union so both parse cleanly.
export async function GetPurchasesList(
  spaceId: string,
  subscribeStatus?: string
): Promise<
  ApiResponse<
    { items: TSubscriptionType[]; total_count: number } | TSubscriptionType[]
  >
> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.PURCHASES_LIST(spaceId, subscribeStatus)
  );
  return response;
}

// Failed Payments (Confluence "API implementations for Invoice and Payment
// method page"): authoritative count behind the failed-payment banner.
export async function GetFailedCount(): Promise<
  ApiResponse<{ failed_count: number }>
> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.FAILED_COUNT
  );
  return response;
}

// List Invoices for a single subscription. Spec keys this by `purchase_id`.
// `limit` defaults to 50 (spec mandates no value); pagination via `skip` is
// available if a subscription ever exceeds it.
export async function GetTransactionsByPurchase(
  purchaseId: string,
  limit = 50,
  skip = 0
): Promise<ApiResponse<TTransactionList>> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.TRANSACTIONS_BY_PURCHASE(purchaseId, limit, skip)
  );
  return response;
}
